import { useMemo, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./Dashboard.css";

export default function LineChart({ data }) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 250 });

  // Update dimensions based on container size
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width } = entries[0].contentRect;
        // Maintain aspect ratio and set minimum dimensions
        const chartWidth = Math.max(400, width - 40); // Removed max constraint, increased min
        const chartHeight = Math.max(250, chartWidth * 0.5); // Increased minimum height
        setDimensions({ width: chartWidth, height: chartHeight });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Parse and group data by month
  const parsedData = useMemo(
    () => {
      if (!data || data.length === 0) {
        return [];
      }

      // Group by year-month
      const grouped = {};
      data.forEach((d) => {
        const date = new Date(d.month);
        const year = date.getFullYear();
        const month = date.getMonth();
        const key = `${year}-${String(month + 1).padStart(2, '0')}`;

        if (!grouped[key]) {
          grouped[key] = {
            date: new Date(year, month, 1),
            quantity: 0
          };
        }
        grouped[key].quantity += d.quantity;
      });

      // Convert to array and sort by date
      return Object.values(grouped).sort((a, b) => a.date - b.date);
    },
    [data]
  );

  if (!parsedData || parsedData.length === 0) {
    return (
      <div>
        <p className="dashboard-no-data-text">No data available.</p>
      </div>
    );
  }

  const maxValue = Math.max(...parsedData.map((d) => d.quantity));
  const { width, height } = dimensions;

  // Calculate dynamic padding based on max value length
  const maxValueStr = Math.round(maxValue).toLocaleString();
  const yLabelWidth = Math.max(35, maxValueStr.length * 7);
  const padding = { top: 20, right: 20, bottom: 40, left: yLabelWidth + 8 };

  // Calculate date range
  const minDate = parsedData[0].date;
  const maxDateData = parsedData[parsedData.length - 1].date;

  // For year view, extend maxDate to end of the year to properly space year labels
  const maxDate = new Date(maxDateData.getFullYear(), 11, 31); // December 31st of last data year

  // Calculate total months in the range (to end of last year, not just last data point)
  const totalMonths = (maxDate.getFullYear() - minDate.getFullYear()) * 12 +
                      (maxDate.getMonth() - minDate.getMonth());

  // Determine if we should show months or years (less than 18 months = 1.5 years)
  const showMonths = totalMonths < 18;

  // Create labels based on the time range
  const timeLabels = showMonths
    ? parsedData.map(d => ({
        label: d.date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        date: d.date,
      }))
    : (() => {
        // Get all unique years from data
        const allYears = [...new Set(parsedData.map(d => d.date.getFullYear()))].sort((a, b) => a - b);

        return allYears.map(year => ({
          label: year.toString(),
          year: year,
        }));
      })();

  // Prepare points for plot based on actual date position
  const plotWidth = width - padding.left - padding.right;

  const points = parsedData.map((d) => {
    // Calculate months from start date
    const monthsFromStart = (d.date.getFullYear() - minDate.getFullYear()) * 12 +
                            (d.date.getMonth() - minDate.getMonth());

    // Position based on actual time position
    let xPos;
    if (totalMonths === 0) {
      // Single month case - center it
      xPos = padding.left + plotWidth / 2;
    } else {
      // Position proportionally based on time
      xPos = padding.left + (monthsFromStart / totalMonths) * plotWidth;
    }

    return {
      x: xPos,
      y: height - padding.bottom - (d.quantity / maxValue) * (height - padding.top - padding.bottom),
      date: d.date,
    };
  });

  // Calculate polynomial regression for trendline (quadratic fit)
  const calculateTrendline = (points) => {
    const n = points.length;
    if (n < 2) return null;

    // Use quadratic regression (degree 2) for better fit
    // y = a*x^2 + b*x + c
    const degree = Math.min(3, Math.floor(n / 2)); // Use cubic for more data, quadratic otherwise

    // Normalize x values to [0, 1] range for numerical stability
    const xValues = points.map((_, i) => i / (n - 1));
    const yValues = points.map(p => p.y);

    // Build the matrix for polynomial regression
    const matrix = [];
    const vector = [];

    for (let i = 0; i <= degree; i++) {
      const row = [];
      let sum = 0;

      for (let j = 0; j <= degree; j++) {
        let matrixSum = 0;
        for (let k = 0; k < n; k++) {
          matrixSum += Math.pow(xValues[k], i + j);
        }
        row.push(matrixSum);
      }

      for (let k = 0; k < n; k++) {
        sum += yValues[k] * Math.pow(xValues[k], i);
      }

      matrix.push(row);
      vector.push(sum);
    }

    // Solve using Gaussian elimination
    const coefficients = gaussianElimination(matrix, vector);

    return { coefficients, degree };
  };

  // Gaussian elimination to solve system of linear equations
  const gaussianElimination = (matrix, vector) => {
    const n = matrix.length;
    const augmented = matrix.map((row, i) => [...row, vector[i]]);

    // Forward elimination
    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
          maxRow = k;
        }
      }
      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

      // Make all rows below this one 0 in current column
      for (let k = i + 1; k < n; k++) {
        const factor = augmented[k][i] / augmented[i][i];
        for (let j = i; j <= n; j++) {
          augmented[k][j] -= factor * augmented[i][j];
        }
      }
    }

    // Back substitution
    const solution = new Array(n);
    for (let i = n - 1; i >= 0; i--) {
      solution[i] = augmented[i][n];
      for (let j = i + 1; j < n; j++) {
        solution[i] -= augmented[i][j] * solution[j];
      }
      solution[i] /= augmented[i][i];
    }

    return solution;
  };

  const trendline = calculateTrendline(points);

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="chart-wrapper" ref={containerRef}>
      <svg width={width} height={height} className="chart-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid and Y-axis labels */}
        {yTicks.map((r, i) => {
          const yPos = height - padding.bottom - r * (height - padding.top - padding.bottom);
          const valueLabel = Math.round(maxValue * r).toLocaleString();
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={yPos}
                x2={width - padding.right}
                y2={yPos}
                stroke="rgba(255,255,255,0.1)"
                strokeDasharray="2,2"
              />
              <text
                x={padding.left - 8}
                y={yPos + 4}
                textAnchor="end"
                fill="rgba(255,255,255,0.7)"
                fontSize={10}
              >
                {valueLabel}
              </text>
            </g>
          );
        })}

        {/* Trendline */}
        {trendline && (() => {
          // Generate smooth curve points for the polynomial trendline
          const curvePoints = [];
          const steps = Math.max(50, points.length * 3); // More steps for smoother curve

          for (let i = 0; i <= steps; i++) {
            const t = i / steps; // Normalized x value [0, 1]

            // Calculate polynomial value: sum of coefficients * t^power
            let yValue = 0;
            for (let j = 0; j <= trendline.degree; j++) {
              yValue += trendline.coefficients[j] * Math.pow(t, j);
            }

            // Map t back to actual x position
            const xPos = points[0].x + t * (points[points.length - 1].x - points[0].x);

            curvePoints.push({ x: xPos, y: yValue });
          }

          // Create SVG path from curve points
          const pathD = curvePoints
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
            .join(' ');

          return (
            <path
              d={pathD}
              fill="none"
              stroke="#00EF97"
              strokeWidth={2}
              strokeOpacity={0.6}
              strokeDasharray="5,5"
            />
          );
        })()}

        {/* Data points (scatter plot) */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={5} fill="#00EF97" stroke="#fff" strokeWidth={2} />
        ))}

        {/* X-axis labels - show months or years based on time range */}
        {showMonths
          ? // Show month labels for each data point
            timeLabels.map((item, i) => (
              <text
                key={i}
                x={points[i].x}
                y={height - padding.bottom + 25}
                textAnchor="middle"
                fill="rgba(255,255,255,0.7)"
                fontSize={10}
              >
                {item.label}
              </text>
            ))
          : // Show year labels - positioned at start of each year
            timeLabels.map((item) => {
              // Position label at January 1st of each year
              const yearStartDate = new Date(item.year, 0, 1);

              // Calculate months from minDate to this year's start
              const monthsFromStart = (yearStartDate.getFullYear() - minDate.getFullYear()) * 12 +
                                      (yearStartDate.getMonth() - minDate.getMonth());

              let xPos;
              if (totalMonths === 0) {
                xPos = padding.left + plotWidth / 2;
              } else {
                xPos = padding.left + (monthsFromStart / totalMonths) * plotWidth;
              }

              return (
                <text
                  key={item.year}
                  x={xPos}
                  y={height - padding.bottom + 25}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.7)"
                  fontSize={10}
                >
                  {item.label}
                </text>
              );
            })}
      </svg>
    </div>
  );
}

LineChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
    })
  ),
};
